'use client';

import { ComponentType, ReactNode } from 'react';
import { redirect, usePathname } from 'next/navigation';

import { ROUTES } from 'constant/routes';

import { selectHasAuthCredentials } from 'store/selectors/authSelectors';
import { useAppSelector } from 'store/hooks';

import {
  buildLoginUrlWithReturnPath,
  rememberCurrentPageForPostAuthLogin
} from 'utils/postAuthReturnUrl';

import { Loader } from 'ui/Loader';

interface AuthProps {
  children: ReactNode;
}

const withPrivateRouting = (WrappedComponent: ComponentType<AuthProps>) => {
  const AuthComponent = ({ children, ...props }: AuthProps) => {
    const pathname = usePathname();
    const { user, isLoading } = useAppSelector((state) => state.user);
    const hasCredentials = useAppSelector(selectHasAuthCredentials);

    const isSessionRestoring = Boolean(hasCredentials && !user) || isLoading;

    if (isSessionRestoring) {
      return <Loader fixed />;
    }

    if (!user || !hasCredentials) {
      rememberCurrentPageForPostAuthLogin();
      const returnPath =
        typeof window !== 'undefined'
          ? `${window.location.pathname}${window.location.search}`
          : pathname;
      redirect(buildLoginUrlWithReturnPath(ROUTES.login, returnPath));
    }

    return (
      <>
        <WrappedComponent {...props}>{children}</WrappedComponent>
        {isLoading && <Loader fixed bg='blackOpacity.10' />}
      </>
    );
  };

  return AuthComponent;
};

export default withPrivateRouting;
